import { Clock, SearchLg } from '@untitledui/icons';
import { useCallback } from 'react';

import { usePermissions } from '@/hooks/usePermissions';
import { Button } from '@/lib/base/buttons/button';

import type { CrmObject } from '../../core/types';
import { getEntityTypeConfig } from '../registry/entityTypeRegistry';
import { canResearch } from '../research/ResearchButton';
import { useCreateResearch } from '../research/useCreateResearch';
import { useResearch } from '../research/useResearch';
import {
  ReadMoreText,
  SummaryCardShell,
  SummaryLoadingSkeleton,
} from './summaryPanelShell';

export function ResearchSummaryPanel({ object }: { object: CrmObject }) {
  const recordType: 'contact' | 'company' =
    object.entity_type_name === 'ahoy_company' ? 'company' : 'contact';
  const entityId = object.entity_id ?? '';
  const orgId = object.org_id;

  const { research, isLoading, error } = useResearch({
    orgId,
    entityId,
    recordType,
    enabled: !!entityId,
  });

  const { createResearch, isCreating } = useCreateResearch({
    orgId,
    entityId,
    recordType,
  });
  // Generating research creates an ahoy_research record — a write. A
  // read-only seat still sees any existing summary, just never the CTA.
  const { hasPermission } = usePermissions();
  const canGenerateResearch = hasPermission(
    'ahoy_research',
    'ahoy_object_created'
  );

  const handleGenerate = useCallback(() => {
    void createResearch();
  }, [createResearch]);

  const latest = research[0] ?? null;
  // `research` is sorted by created_at desc, so this is the most recent
  // completed item with a usable summary. Prefer it over the generating
  // state so a newer queued/in-progress record doesn't hide an existing
  // summary.
  const latestComplete =
    research.find(
      r => r.status === 'complete' && !!r.summary && !!r.summary.trim()
    ) ?? null;
  const displayName = getEntityTypeConfig(
    object.entity_type_name
  ).getDisplayName(object);

  if (error) {
    return (
      <SummaryCardShell>
        <div
          role='alert'
          aria-label="We couldn't load the summary"
          className='text-body-small text-[var(--color-text-error-primary)]'
        >
          Couldn&apos;t load the AI summary. Try again later.
        </div>
      </SummaryCardShell>
    );
  }

  if (isLoading && !latest) {
    return (
      <SummaryCardShell>
        <SummaryLoadingSkeleton />
      </SummaryCardShell>
    );
  }

  if (latestComplete) {
    return (
      <SummaryCardShell>
        <ReadMoreText text={latestComplete.summary ?? ''} orgId={orgId} />
      </SummaryCardShell>
    );
  }

  if (
    latest &&
    (latest.status === 'in_progress' || latest.status === 'queued')
  ) {
    return (
      <SummaryCardShell>
        <div
          className='flex items-center gap-2 text-body-small text-[var(--color-text-secondary)]'
          aria-live='polite'
          aria-busy='true'
          aria-label='Generating research'
        >
          <Clock className='size-4' aria-hidden='true' />
          <span>Researching…</span>
        </div>
      </SummaryCardShell>
    );
  }

  if (canGenerateResearch && canResearch(object, recordType)) {
    // Compact empty state: text + a tight white-card button on a single
    // row when the column allows it. The button overrides default `sm`
    // padding (`!px-2 !py-1 !text-xs`) so it sits ~24px tall — same
    // proportion as the slideout's Draft email button — keeping the
    // empty state under ~50px total instead of the previous ~150px
    // body-small paragraph + full-size CTA combo.
    return (
      <SummaryCardShell>
        <div className='flex flex-wrap items-center gap-x-3 gap-y-1'>
          <p className='text-body-small text-[var(--color-text-secondary)]'>
            No research yet.
          </p>
          <Button
            color='secondary'
            size='sm'
            iconLeading={SearchLg}
            isLoading={isCreating}
            isDisabled={isCreating}
            onClick={handleGenerate}
            className='!text-xs !px-2 !py-1'
            aria-label={`Generate research about ${displayName}`}
          >
            Generate research
          </Button>
        </div>
      </SummaryCardShell>
    );
  }

  return null;
}
